import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const BOOK_COVER = "https://media-manager-c.questera.ai/greta-media/ae05bbc051fb17176d2c348bf77438a7a494626431a725f22ebe70ac4579d949c16512ea83019a888cf26e0e50680ce7/images/aW1hZ2UvanBlZw==/b61a60ddab35f9b0af6b0aead5859bb1.jpg";

const fadeIn = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } };

const problems = [
  { icon: FiIcons.FiLock, title: "Too Shallow or Too Technical", desc: "One side promises a site in minutes via closed platforms. The other assumes developer knowledge and skips the practical explanations beginners actually need." },
  { icon: FiIcons.FiAlertTriangle, title: "Platform Lock-in", desc: "Domains bundled with builders, content trapped in proprietary systems, and migration that becomes difficult or incomplete. Your site is an asset — treat it like one." },
  { icon: FiIcons.FiTrendingDown, title: "No Real Understanding", desc: "Most tutorials turn readers into passive followers. This book turns you into someone who can make informed decisions, troubleshoot issues, and build with confidence." },
];

const outcomes = [
  "Register a domain through an independent registrar",
  "Choose the right hosting for your needs and future growth",
  "Plan a site structure before building pages",
  "Learn HTML and CSS from absolute zero",
  "Build a hand-coded, mobile-responsive website with semantic structure",
  "Set up WordPress correctly with security, SEO, and backups",
  "Build with accessibility in mind using WCAG 2.2 principles",
];

const faqs = [
  { q: "Is this book for complete beginners?", a: "Yes. No prior coding knowledge is required. Chapter 2 starts from absolute zero in HTML and CSS." },
  { q: "Does the book teach WordPress too?", a: "Yes. Chapter 3 covers WordPress from installation to launch, including settings, security, SEO, backups, and pre-launch checks." },
  { q: "What makes it different from quick tutorials?", a: "It emphasises ownership, understanding, and long-term control rather than speed alone. Every chapter teaches both the action and the reason behind it." },
  { q: "Is this book part of a series?", a: "Yes. Volume 1 covers the full foundation. Volume 2 continues into page builders, advanced SEO, performance, security, analytics, and growth strategy." },
];

export default function Home() {
  return (
    <div className="w-full">
      {/* HERO */}
      <section className="bg-slate-900 text-white pt-20 pb-28 overflow-hidden relative">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop')] bg-cover bg-center opacity-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeIn}>
              <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-300 px-4 py-1.5 rounded-full text-sm font-semibold mb-6 border border-blue-500/30">
                <SafeIcon icon={FiIcons.FiBookOpen} className="text-sm" /> Volume 1 Now Available
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 leading-tight">
                Build a Website You{' '}
                <span className="text-blue-400">Actually Own</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-300 mb-4 leading-relaxed max-w-xl">
                Most people don't want a website for its own sake. They want what a website <em>does</em>: credibility, visibility, customers, and control over how they show up online.
              </p>
              <p className="text-base text-slate-400 mb-8 max-w-xl leading-relaxed">
                Website Blueprint Foundation: Volume 1 is a practical guide for beginners, freelancers, and business owners who want to build a professional website without shortcuts, confusion, or platform lock-in.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/buy" className="bg-blue-600 text-white px-8 py-4 rounded-lg font-bold text-base hover:bg-blue-500 transition-all shadow-[0_0_24px_rgba(37,99,235,0.45)] flex items-center justify-center gap-2">
                  Buy the Book <SafeIcon icon={FiIcons.FiArrowRight} />
                </Link>
                <Link to="/learn" className="bg-white/10 text-white px-8 py-4 rounded-lg font-bold text-base hover:bg-white/20 transition-all border border-white/20 flex items-center justify-center gap-2">
                  <SafeIcon icon={FiIcons.FiList} /> See What's Inside
                </Link>
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-400">
                <span className="flex items-center gap-1.5"><SafeIcon icon={FiIcons.FiCheckCircle} className="text-green-400" /> Kindle — $2.99</span>
                <span className="flex items-center gap-1.5"><SafeIcon icon={FiIcons.FiCheckCircle} className="text-green-400" /> Paperback — $9.99</span>
                <span className="flex items-center gap-1.5"><SafeIcon icon={FiIcons.FiCheckCircle} className="text-green-400" /> Hardcover — $13.99</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.25 }}
              className="flex justify-center items-center relative"
            >
              <div className="absolute w-72 h-72 bg-blue-600 rounded-full blur-3xl opacity-20 -z-0" />
              <div className="relative z-10 drop-shadow-2xl">
                <img
                  src={BOOK_COVER}
                  alt="Website Blueprint Foundation Volume 1 Book Cover"
                  className="w-full max-w-[340px] md:max-w-[400px] h-auto rounded-xl shadow-[0_30px_80px_rgba(0,0,0,0.5)] border border-white/10"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-5">Why Most Website Advice Fails Beginners</h2>
            <p className="text-lg text-slate-600">This book was written to close the gap between shallow tutorials and advice that assumes too much.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {problems.map((p, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }} className="bg-slate-50 p-8 rounded-2xl border border-slate-100">
                <div className="bg-red-100 w-12 h-12 rounded-lg flex items-center justify-center mb-5">
                  <SafeIcon icon={p.icon} className="text-red-600 text-xl" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{p.title}</h3>
                <p className="text-slate-600 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT THIS BOOK HELPS YOU DO */}
      <section className="py-24 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <div className="lg:w-1/2">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-5">What This Book Helps You Do</h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                It combines checklist clarity with plain-language explanations so readers not only know <em>what</em> to do, but understand <em>why</em> each step matters and how to make better decisions as their site grows.
              </p>
              <ul className="space-y-4">
                {outcomes.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <SafeIcon icon={FiIcons.FiCheckCircle} className="text-blue-600 mt-0.5 flex-shrink-0 text-lg" />
                    <span className="text-slate-700 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-10">
                <Link to="/learn" className="text-blue-600 font-semibold hover:text-blue-700 flex items-center gap-2">
                  See full chapter breakdown <SafeIcon icon={FiIcons.FiArrowRight} />
                </Link>
              </div>
            </div>
            <div className="lg:w-1/2 grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 h-40 flex flex-col justify-center">
                  <SafeIcon icon={FiIcons.FiCode} className="text-blue-500 text-3xl mb-3" />
                  <h4 className="font-bold text-slate-900">HTML & CSS</h4>
                  <p className="text-slate-500 text-sm mt-1">From absolute zero.</p>
                </div>
                <div className="bg-blue-600 p-6 rounded-2xl shadow-sm h-48 flex flex-col justify-center text-white">
                  <SafeIcon icon={FiIcons.FiLayout} className="text-blue-200 text-3xl mb-3" />
                  <h4 className="font-bold">WordPress</h4>
                  <p className="text-blue-100 text-sm mt-2">Configured correctly from the start.</p>
                </div>
              </div>
              <div className="space-y-4 mt-8">
                <div className="bg-slate-900 p-6 rounded-2xl shadow-sm h-48 flex flex-col justify-center text-white">
                  <SafeIcon icon={FiIcons.FiServer} className="text-slate-400 text-3xl mb-3" />
                  <h4 className="font-bold">Hosting & Domains</h4>
                  <p className="text-slate-400 text-sm mt-2">True ownership, not rented space.</p>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 h-40 flex flex-col justify-center">
                  <SafeIcon icon={FiIcons.FiEye} className="text-green-500 text-3xl mb-3" />
                  <h4 className="font-bold text-slate-900">Accessibility</h4>
                  <p className="text-slate-500 text-sm mt-1">WCAG 2.2 built in.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT MAKES IT DIFFERENT */}
      <section className="py-24 bg-white border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">What Makes This Book Different</h2>
          <p className="text-lg text-slate-600 mb-12 max-w-2xl mx-auto leading-relaxed">
            This is not a motivational business book disguised as a technical guide, and it is not a "launch in ten minutes" tutorial. It is a structured, annotated system for building a website properly.
          </p>
          <div className="grid md:grid-cols-2 gap-6 text-left">
            {[
              { icon: FiIcons.FiTarget, title: "Do the thing. Understand the why.", desc: "Every chapter follows the same method: action first, then the reason behind it. Readers become confident decision-makers, not passive tutorial followers." },
              { icon: FiIcons.FiShield, title: "Ownership as a core principle", desc: "The book treats platform dependence as a structural vulnerability. Every recommendation prioritises portability, control, and long-term independence." },
              { icon: FiIcons.FiCheckSquare, title: "Annotated checklist format", desc: "Checklists with explanations make the book useful both as a first complete read and as a working reference throughout a real project." },
              { icon: FiIcons.FiTrendingUp, title: "Standards that hold up over time", desc: "Semantic HTML, WCAG 2.2 accessibility, clean WordPress configuration, and SEO foundations — built to last, not built to hack around." },
            ].map((card, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="bg-slate-50 p-6 rounded-2xl border border-slate-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-blue-100 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={card.icon} className="text-blue-600 text-lg" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-lg">{card.title}</h3>
                </div>
                <p className="text-slate-600 leading-relaxed">{card.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FORMATS / PRICING */}
      <section className="py-24 bg-slate-900 text-white" id="buy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Choose Your Format</h2>
            <p className="text-lg text-slate-400">Available in three formats to suit how you read, study, and build.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-slate-800 rounded-2xl p-8 border border-slate-700 flex flex-col">
              <h3 className="text-xl font-bold text-slate-300 mb-2">Kindle Edition</h3>
              <div className="text-4xl font-extrabold text-white mb-2">$2.99</div>
              <p className="text-slate-400 text-sm mb-6">Instant delivery. Read on any device.</p>
              <ul className="space-y-2 mb-8 flex-grow text-slate-400 text-sm">
                <li className="flex items-center gap-2"><SafeIcon icon={FiIcons.FiCheck} className="text-blue-400" /> Read anywhere, anytime</li>
                <li className="flex items-center gap-2"><SafeIcon icon={FiIcons.FiCheck} className="text-blue-400" /> Searchable text</li>
                <li className="flex items-center gap-2"><SafeIcon icon={FiIcons.FiCheck} className="text-blue-400" /> Instant access</li>
              </ul>
              <Link to="/buy" className="block w-full py-3 px-4 bg-slate-700 hover:bg-slate-600 text-center rounded-lg font-semibold transition-colors">Buy Kindle</Link>
            </div>
            <div className="bg-blue-600 rounded-2xl p-8 border border-blue-500 flex flex-col relative transform md:-translate-y-4 shadow-2xl">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-yellow-400 text-yellow-900 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wide">Most Popular</div>
              <h3 className="text-xl font-bold text-blue-100 mb-2">Paperback</h3>
              <div className="text-4xl font-extrabold text-white mb-2">$9.99</div>
              <p className="text-blue-200 text-sm mb-6">Great for annotating. Keep it at your desk.</p>
              <ul className="space-y-2 mb-8 flex-grow text-blue-100 text-sm">
                <li className="flex items-center gap-2"><SafeIcon icon={FiIcons.FiCheck} className="text-white" /> High-quality print</li>
                <li className="flex items-center gap-2"><SafeIcon icon={FiIcons.FiCheck} className="text-white" /> Great for highlighting</li>
                <li className="flex items-center gap-2"><SafeIcon icon={FiIcons.FiCheck} className="text-white" /> Ideal working reference</li>
              </ul>
              <Link to="/buy" className="block w-full py-3 px-4 bg-white text-blue-600 hover:bg-slate-100 text-center rounded-lg font-bold transition-colors">Buy Paperback</Link>
            </div>
            <div className="bg-slate-800 rounded-2xl p-8 border border-slate-700 flex flex-col">
              <h3 className="text-xl font-bold text-slate-300 mb-2">Hardcover</h3>
              <div className="text-4xl font-extrabold text-white mb-2">$13.99</div>
              <p className="text-slate-400 text-sm mb-6">Premium binding. Built to last.</p>
              <ul className="space-y-2 mb-8 flex-grow text-slate-400 text-sm">
                <li className="flex items-center gap-2"><SafeIcon icon={FiIcons.FiCheck} className="text-blue-400" /> Durable hardback cover</li>
                <li className="flex items-center gap-2"><SafeIcon icon={FiIcons.FiCheck} className="text-blue-400" /> Premium paper quality</li>
                <li className="flex items-center gap-2"><SafeIcon icon={FiIcons.FiCheck} className="text-blue-400" /> Ultimate reference copy</li>
              </ul>
              <Link to="/buy" className="block w-full py-3 px-4 bg-slate-700 hover:bg-slate-600 text-center rounded-lg font-semibold transition-colors">Buy Hardcover</Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-slate-50 border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">Frequently Asked Questions</h2>
          <div className="space-y-5">
            {faqs.map((faq, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.08 }} className="bg-white p-6 rounded-xl border border-slate-200">
                <h4 className="text-lg font-bold text-slate-900 mb-2">{faq.q}</h4>
                <p className="text-slate-600 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/buy" className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-lg font-bold hover:bg-blue-700 transition-colors">
              Buy Website Blueprint Foundation: Volume 1 <SafeIcon icon={FiIcons.FiArrowRight} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}